import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  Tooltip,
  Chip,
  Avatar,
  Divider,
} from "@mui/material";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LiveTv,
  History,
  CalendarToday,
  AccessTime,
  LocationOn,
  PlayCircleOutline,
  FilterList,
} from "@mui/icons-material";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";
import {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../types/thumbnail-template";
import StreamCard from "./components/StreamCardThumbnail";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeceasedPerson {
  id: number;
  first_name: string;
  second_name?: string | null;
  surname: string;
  person_image?: string | null;
}

interface ThumbnailRecord {
  id: number;
  deceased: DeceasedPerson[];
  stream?: { id: number };
  thumbnail: IThumbnailTemplate | null;
}

interface StreamsApiResponse {
  count?: number;
  results?: ApiStream[];
  next?: string | null;
  previous?: string | null;
}

interface ApiStream {
  id: number;
  title: string;
  date: string;
  start: string;
  end: string;
  status: string;
  youtube_stream_link?: string | null;
  website_stream_link?: string | null;
  address_one?: string | null;
  suburb?: string | null;
  city?: string | null;
  stream_thumbnail_background_image?: string | null;
  booking?: {
    deceased?: DeceasedPerson[];
    booking_type?: string;
    title?: string;
  } | null;
  // Embedded by the /dashboard-carousel/ endpoint so no separate thumbnail fetch is needed.
  deceased_thumbnail?: ThumbnailRecord | null;
}

// ─── Date Filter ─────────────────────────────────────────────────────────────

type DateFilterValue = "today" | "3days" | "week" | "month" | "all" | "custom";

interface DateFilterOption {
  value: DateFilterValue;
  label: string;
}

const DATE_FILTER_OPTIONS: DateFilterOption[] = [
  { value: "today", label: "Today" },
  { value: "3days", label: "Next 3 days" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "all", label: "All upcoming" },
  { value: "custom", label: "Custom range" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const padZ = (n: number) => String(n).padStart(2, "0");

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}
function hasStreamEnded(dateStr: string, endStr: string): boolean {
  return new Date(`${dateStr}T${endStr}`).getTime() < Date.now();
}

function matchesDateFilter(
  s: ApiStream,
  filter: DateFilterValue,
  customRange?: { from: string; to: string },
): boolean {
  const startDt = new Date(`${s.date}T${s.start}`);
  const now = new Date();
  switch (filter) {
    case "today":
      return startDt >= startOfDay(now) && startDt <= endOfDay(now);
    case "3days":
      return startDt >= startOfDay(now) && startDt <= endOfDay(addDays(now, 2));
    case "week": {
      const dayOfWeek = now.getDay();
      const weekStart = startOfDay(addDays(now, -dayOfWeek));
      const weekEnd = endOfDay(addDays(weekStart, 6));
      return startDt >= weekStart && startDt <= weekEnd;
    }
    case "month":
      return (
        startDt.getFullYear() === now.getFullYear() &&
        startDt.getMonth() === now.getMonth()
      );
    case "custom": {
      if (!customRange?.from && !customRange?.to) return true;
      const from = customRange.from
        ? startOfDay(new Date(customRange.from))
        : new Date(0);
      const to = customRange.to
        ? endOfDay(new Date(customRange.to))
        : new Date(8_640_000_000_000_000);
      return startDt >= from && startDt <= to;
    }
    case "all":
    default:
      return true;
  }
}

function getCountdown(dateStr: string, startStr: string) {
  const diff = new Date(`${dateStr}T${startStr}`).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hrs: 0, min: 0, sec: 0, past: true };
  return {
    days: Math.floor(diff / 86_400_000),
    hrs: Math.floor((diff % 86_400_000) / 3_600_000),
    min: Math.floor((diff % 3_600_000) / 60_000),
    sec: Math.floor((diff % 60_000) / 1_000),
    past: false,
  };
}

function formatDate(dateStr?: string, startStr?: string): string {
  if (!dateStr || !startStr) return "Date/Time TBA";
  try {
    return new Date(`${dateStr}T${startStr}`).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "Invalid Date";
  }
}

function formatShortDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatTime(timeStr?: string): string {
  if (!timeStr) return "";
  try {
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(Number(h), Number(m));
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return timeStr;
  }
}

function getStatusBadge(status?: string): string | null {
  const s = (status || "").toLowerCase();
  if (s.includes("lost")) return "LOST SALE";
  if (s.includes("cancel")) return "CANCELLED";
  return null;
}

function StatusStamp({ label }: { label: string }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: "58%",
        transform: "translate(-50%, -50%) rotate(-10deg)",
        zIndex: 6,
        border: "3px solid #d32f2f",
        borderRadius: "6px",
        px: 2,
        py: 0.5,
        pointerEvents: "none",
      }}
    >
      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 900,
          color: "#d32f2f",
          letterSpacing: 2,
          textTransform: "uppercase",
          opacity: 0.85,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const getVenue = (s: ApiStream): string =>
  [s.address_one, s.suburb, s.city].filter(Boolean).join(", ") || "Venue TBA";

const getDeceasedName = (s: ApiStream): string => {
  const deceased = s.booking?.deceased;
  if (!deceased?.length) return s.title;
  const d = deceased[0];
  return `${d.first_name}${d.second_name ? " " + d.second_name : ""} ${d.surname}`;
};

// ─── CountdownTimer ───────────────────────────────────────────────────────────
// ORIGINAL - UNCHANGED

function CountdownTimer({ date, start }: { date: string; start: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1_000);
    return () => clearInterval(id);
  }, []);
  const { days, hrs, min, sec, past } = getCountdown(date, start);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "background.paper",
        borderRadius: "0 0 14px 14px",
        px: 3,
        py: 1.5,
        border: "1px solid",
        borderTop: "none",
        borderColor: "divider",
        boxShadow: "0 4px 12px rgba(0,0,0,0.07)",
      }}
    >
      {past ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 52,
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: 1.5,
            }}
          >
            STREAMING NOW
          </Typography>
        </Box>
      ) : (
        [
          { val: days, label: "DAYS" },
          { val: hrs, label: "HRS" },
          { val: min, label: "MIN" },
          { val: sec, label: "SEC" },
        ].map(({ val, label }, i) => (
          <React.Fragment key={label}>
            {i > 0 && (
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 1.5,
                  px: 0.3,
                }}
              >
                :
              </Typography>
            )}
            <Box sx={{ textAlign: "center", minWidth: 40 }}>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: "primary.main",
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {padZ(val)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 8,
                  color: "text.secondary",
                  letterSpacing: 1.5,
                  mt: 0.3,
                }}
              >
                {label}
              </Typography>
            </Box>
          </React.Fragment>
        ))
      )}
    </Box>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
// ORIGINAL - UNCHANGED

function CardSkeleton({ width }: { width: string }) {
  return (
    <Box sx={{ flexShrink: 0, width }}>
      <Skeleton
        variant="rounded"
        height={160}
        sx={{ borderRadius: "14px 14px 0 0" }}
      />
      <Skeleton
        variant="rounded"
        height={58}
        sx={{ borderRadius: "0 0 14px 14px", mt: "1px" }}
      />
    </Box>
  );
}

// ─── Nav Arrow ────────────────────────────────────────────────────────────────
// ORIGINAL - UNCHANGED

function NavArrow({
  direction,
  disabled,
  onClick,
  colors,
}: {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
  colors: ReturnType<typeof tokens>;
}) {
  return (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        bgcolor: disabled ? "action.disabledBackground" : colors.primary[400],
        color: disabled ? "action.disabled" : "#fff",
        borderRadius: "10px",
        flexShrink: 0,
        boxShadow: disabled ? "none" : "0 2px 8px rgba(0,0,0,0.18)",
        "&:hover": {
          bgcolor: disabled ? undefined : colors.primary[300],
          transform: disabled ? undefined : "scale(1.08)",
        },
        transition: "all 0.18s ease",
      }}
    >
      {direction === "left" ? (
        <KeyboardArrowLeft fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
}

// ─── Recent Stream Row ────────────────────────────────────────────────────────
function RecentStreamRow({
  stream,

  isDark,
  onClick,
}: {
  stream: ApiStream;
  colors: ReturnType<typeof tokens>;
  isDark: boolean;
  onClick?: () => void;
}) {
  const name = getDeceasedName(stream);
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const venue = getVenue(stream);
  const bookingType = stream.booking?.booking_type || "";
  const isFuneral =
    bookingType.toLowerCase().includes("funeral") ||
    bookingType.toLowerCase().includes("burial");
  const accentColor = isFuneral ? "#6366f1" : "#0ea5e9";
  const deceased = stream.booking?.deceased?.[0];
  const hasPhoto = !!deceased?.person_image;

  // NEW: lost/cancelled badge
  const statusBadge = getStatusBadge(stream.status);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.75,
        cursor: "pointer",
        opacity: statusBadge ? 0.62 : 1, // NEW: mute the row visually
        transition: "all 0.18s ease",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
          "& .play-icon": { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Avatar
        src={hasPhoto ? deceased!.person_image! : undefined}
        sx={{
          width: 42,
          height: 42,
          flexShrink: 0,
          bgcolor: accentColor + "22",
          color: accentColor,
          fontWeight: 700,
          fontSize: 14,
          border: `2px solid ${accentColor}33`,
        }}
      >
        {!hasPhoto && initials}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 650,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.3,
          }}
        >
          {name}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 0.4,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <CalendarToday sx={{ fontSize: 11, color: "text.disabled" }} />
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {formatShortDate(stream.date)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <AccessTime sx={{ fontSize: 11, color: "text.disabled" }} />
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {formatTime(stream.start)}
            </Typography>
          </Box>
          {venue !== "Venue TBA" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                maxWidth: 200,
                overflow: "hidden",
              }}
            >
              <LocationOn
                sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {venue}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* NEW: status badge takes priority over the type chip when present */}
      {statusBadge ? (
        <Chip
          label={statusBadge}
          size="small"
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.4,
            bgcolor: "#d32f2f18",
            color: "#d32f2f",
            border: "1px solid #d32f2f50",
            "& .MuiChip-label": { px: 1 },
            flexShrink: 0,
          }}
        />
      ) : (
        <Chip
          label={isFuneral ? "Funeral" : "Conference"}
          size="small"
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.3,
            bgcolor: accentColor + "18",
            color: accentColor,
            border: `1px solid ${accentColor}30`,
            "& .MuiChip-label": { px: 1 },
            flexShrink: 0,
          }}
        />
      )}

      {!statusBadge &&
        (stream.website_stream_link || stream.youtube_stream_link) && (
          <PlayCircleOutline
            className="play-icon"
            sx={{
              fontSize: 20,
              color: accentColor,
              opacity: 0,
              transform: "scale(0.85)",
              transition: "all 0.18s ease",
              flexShrink: 0,
            }}
          />
        )}
    </Box>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
// ORIGINAL - UNCHANGED

const VISIBLE_H = 3;
const GAP = 16;
const PEEK_DIVISOR = 3.3;
const CARD_WIDTH_H = `calc(${100 / PEEK_DIVISOR}% - ${(GAP * (PEEK_DIVISOR - 1)) / PEEK_DIVISOR}px)`;

// ─── Main Component ───────────────────────────────────────────────────────────

interface HorizontalStreamCarouselProps {
  streamsEndpoint?: string;
  defaultDateFilter?: DateFilterValue;
}

const HorizontalStreamCarousel: React.FC<HorizontalStreamCarouselProps> = ({
  streamsEndpoint = "/api/streams/stream/dashboard-carousel/",
  defaultDateFilter = "all",
}) => {
  const [allStreams, setAllStreams] = useState<ApiStream[]>([]);
  const [recentStreams, setRecentStreams] = useState<ApiStream[]>([]);
  const [thumbMap, setThumbMap] = useState<Map<number, ThumbnailRecord>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [dateFilter, setDateFilter] =
    useState<DateFilterValue>(defaultDateFilter);
  const [customRange, setCustomRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });

  const trackRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const [recentPage, setRecentPage] = useState(0);

  // ── Fetch upcoming ─────────────────────────────────────────────────────────
  // ORIGINAL - UNCHANGED
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        // Single consolidated endpoint: each stream already carries its matching
        // deceased_thumbnail, so no separate thumbnail pagination is needed.
        const fetchedStreams: ApiStream[] = [];
        let nextUrl: string | null =
          `${streamsEndpoint}?filter=upcoming&page_size=100`;
        while (nextUrl !== null) {
          const currentUrl: string = nextUrl;
          const res: { data: StreamsApiResponse } =
            await apiService.get(currentUrl);
          if (cancelled) return;
          const page: ApiStream[] = Array.isArray(res.data)
            ? (res.data as unknown as ApiStream[])
            : (res.data?.results ?? []);
          fetchedStreams.push(...page);
          nextUrl = res.data?.next ?? null;
        }

        if (cancelled) return;

        const active = fetchedStreams
          .filter((s) => !hasStreamEnded(s.date, s.end))
          .sort(
            (a, b) =>
              new Date(`${a.date}T${a.start}`).getTime() -
              new Date(`${b.date}T${b.start}`).getTime(),
          );

        setAllStreams(active);
        // Merge embedded thumbnails into the shared map (race-safe vs. the recent effect).
        setThumbMap((prev) => {
          const m = new Map(prev);
          active.forEach((s) => {
            if (s.deceased_thumbnail) m.set(s.id, s.deceased_thumbnail);
          });
          return m;
        });
      } catch {
        if (!cancelled) setError("Failed to load streams.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [streamsEndpoint]);

  // ── Fetch recent (ended) ───────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchRecent = async () => {
      try {
        setRecentLoading(true);
        // Same consolidated endpoint: filter=recent returns past-dated streams
        // (most recent first) with their thumbnails embedded.
        const res: { data: StreamsApiResponse } = await apiService.get(
          `${streamsEndpoint}?filter=recent&page_size=50`,
        );
        if (cancelled) return;
        const page: ApiStream[] = Array.isArray(res.data)
          ? (res.data as unknown as ApiStream[])
          : (res.data?.results ?? []);

        const ended = page.slice(0, 50);

        if (!cancelled) {
          setRecentStreams(ended);
          // Merge embedded thumbnails into the shared map (race-safe vs. upcoming effect).
          setThumbMap((prev) => {
            const m = new Map(prev);
            ended.forEach((s) => {
              if (s.deceased_thumbnail) m.set(s.id, s.deceased_thumbnail);
            });
            return m;
          });
        }
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    };
    fetchRecent();
    return () => {
      cancelled = true;
    };
  }, [streamsEndpoint]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const streams = allStreams.filter((s) =>
    matchesDateFilter(s, dateFilter, customRange),
  );

  const handleFilterChange = useCallback((value: DateFilterValue) => {
    setDateFilter(value);
    setOffset(0);
  }, []);

  // ── Scroll sync ────────────────────────────────────────────────────────────
  // ORIGINAL - UNCHANGED
  useEffect(() => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    const target = el.children[offset] as HTMLElement | undefined;
    if (!target) return;
    el.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
  }, [offset]);

  // ── Nav ────────────────────────────────────────────────────────────────────
  // ORIGINAL - UNCHANGED
  const canPrev = offset > 0;
  const canNext = offset + VISIBLE_H < streams.length;
  const goBack = () => setOffset((o) => Math.max(0, o - 1));
  const goForward = () =>
    setOffset((o) => Math.min(streams.length - VISIBLE_H, o + 1));

  // ── Card builder ───────────────────────────────────────────────────────────
  // ORIGINAL - UNCHANGED
  const buildCard = (s: ApiStream) => {
    const thumbRec = thumbMap.get(s.id);
    const cardThumbRec =
      thumbRec && thumbRec.thumbnail
        ? {
            thumbnail: thumbRec.thumbnail,
            deceased: thumbRec.deceased?.map((d) => ({
              id: d.id,
              first_name: d.first_name,
              surname: d.surname,
              second_name: d.second_name || "",
              date_of_birth: "",
              date_of_death: "",
              gender: "",
              person_image: d.person_image || "",
            })),
          }
        : {
            thumbnail: {
              category: s.booking?.booking_type || "conference",
              background_image: "",
            } as IThumbnailTemplate,
            deceased: s.booking?.deceased?.map((d) => ({
              id: d.id,
              first_name: d.first_name,
              surname: d.surname,
              second_name: d.second_name || "",
              date_of_birth: "",
              date_of_death: "",
              gender: "",
              person_image: d.person_image || "",
            })),
          };

    const streamData: IStreamData = {
      id: String(s.id),
      title: s.title,
      date: s.date,
      website_stream_link: s.website_stream_link || s.youtube_stream_link || "",
      start_time: s.start,
      venue_name: getVenue(s),
      booking: s.booking
        ? {
            deceased: s.booking.deceased?.map((d) => ({
              id: d.id,
              first_name: d.first_name,
              surname: d.surname,
              second_name: d.second_name || "",
              person_image: d.person_image || "",
              date_of_birth: "",
              date_of_death: "",
              gender: "",
            })),
          }
        : undefined,
    };
    return { streamData, cardThumbRec };
  };

  // ORIGINAL - UNCHANGED
  const cardWrapSx = {
    flexShrink: 0,
    scrollSnapAlign: "start" as const,
    borderRadius: "14px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%" }}>
      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — UPCOMING STREAMS
          Only the header/filter UI is new. The carousel body below
          is 100% original, zero changes.
      ══════════════════════════════════════════════════════════ */}

      {/* ── NEW: Refined header panel ── */}
      <Box
        sx={{
          mb: 2,
          borderRadius: "14px",
          border: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
        }}
      >
        {/* Title row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            pt: 1.75,
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 3,
                height: 26,
                borderRadius: "2px",
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 750,
                fontSize: "1rem",
                letterSpacing: "-0.2px",
              }}
            >
              Upcoming Streams
            </Typography>
            {!loading && streams.length > 0 && (
              <Chip
                icon={<LiveTv sx={{ fontSize: "13px !important" }} />}
                label={`${streams.length} scheduled`}
                size="small"
                sx={{
                  height: 21,
                  fontSize: 10.5,
                  fontWeight: 650,
                  bgcolor: `${colors.primary[400]}18`,
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: `${colors.primary[400]}35`,
                  "& .MuiChip-icon": { color: "primary.main" },
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Box>

          {!loading && streams.length > VISIBLE_H && (
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Tooltip title="Previous" placement="top">
                <span>
                  <NavArrow
                    direction="left"
                    disabled={!canPrev}
                    onClick={goBack}
                    colors={colors}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Next" placement="top">
                <span>
                  <NavArrow
                    direction="right"
                    disabled={!canNext}
                    onClick={goForward}
                    colors={colors}
                  />
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Filter row */}
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mr: 0.25,
              color: "text.disabled",
            }}
          >
            <FilterList sx={{ fontSize: 13 }} />
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Filter
            </Typography>
          </Box>

          {DATE_FILTER_OPTIONS.map((opt) => {
            const isActive = dateFilter === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => handleFilterChange(opt.value)}
                sx={{
                  height: 26,
                  fontSize: 11.5,
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                  borderRadius: "8px",
                  border: "1px solid",
                  transition: "all 0.15s ease",
                  ...(isActive
                    ? {
                        bgcolor: "primary.main",
                        color: "#fff",
                        borderColor: "primary.main",
                        boxShadow: `0 2px 8px ${colors.primary[400]}55`,
                        "&:hover": { bgcolor: "primary.dark" },
                      }
                    : {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.03)",
                        color: "text.secondary",
                        borderColor: "divider",
                        "&:hover": {
                          bgcolor: `${colors.primary[400]}12`,
                          borderColor: `${colors.primary[400]}50`,
                          color: "primary.main",
                        },
                      }),
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            );
          })}

          {dateFilter === "custom" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                ml: 0.5,
                flexWrap: "wrap",
              }}
            >
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
              <Tooltip title="Close custom range">
                <IconButton
                  size="small"
                  onClick={() => {
                    handleFilterChange("all");
                    setCustomRange({ from: "", to: "" });
                  }}
                  sx={{
                    width: 20,
                    height: 20,
                    color: "text.disabled",
                    "&:hover": { color: "text.primary" },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, lineHeight: 1, fontWeight: 300 }}
                  >
                    ✕
                  </Typography>
                </IconButton>
              </Tooltip>
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                From
              </Typography>
              <Box
                component="input"
                type="date"
                value={customRange.from}
                max={customRange.to || undefined}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomRange((r) => ({ ...r, from: e.target.value }));
                  setOffset(0);
                }}
                sx={{
                  height: 26,
                  px: 1,
                  fontSize: 11.5,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "background.paper",
                  color: "text.primary",
                  outline: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:focus": { borderColor: "primary.main" },
                }}
              />
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                To
              </Typography>
              <Box
                component="input"
                type="date"
                value={customRange.to}
                min={customRange.from || undefined}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomRange((r) => ({ ...r, to: e.target.value }));
                  setOffset(0);
                }}
                sx={{
                  height: 26,
                  px: 1,
                  fontSize: 11.5,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "background.paper",
                  color: "text.primary",
                  outline: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:focus": { borderColor: "primary.main" },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ══ CAROUSEL BODY — 100% ORIGINAL CODE, ZERO CHANGES ══ */}
      {loading ? (
        <Box sx={{ display: "flex", gap: `${GAP}px` }}>
          <CardSkeleton width={CARD_WIDTH_H} />
          <CardSkeleton width={CARD_WIDTH_H} />
          <CardSkeleton width={CARD_WIDTH_H} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "error.light",
          }}
        >
          <Typography color="error" fontWeight={600}>
            {error}
          </Typography>
        </Box>
      ) : streams.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 7,
            px: 3,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <LiveTv sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
          <Typography
            sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500 }}
          >
            No streams scheduled for this period.
          </Typography>
          <Typography sx={{ color: "text.disabled", fontSize: 12, mt: 0.5 }}>
            Try a different date range.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative" }}>
          {/* Left gradient overlay */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 2,
              width: 40,
              background:
                "linear-gradient(to right, rgba(0,0,0,0.45) 0%, transparent 100%)",
              borderRadius: "14px 0 0 14px",
              opacity: canPrev ? 1 : 0,
              pointerEvents: "none",
              transition: "opacity 0.25s ease",
            }}
          />
          {/* Track */}
          <Box
            ref={trackRef}
            sx={{
              display: "flex",
              gap: `${GAP}px`,
              overflowX: "scroll",
              scrollBehavior: "smooth",
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {streams.map((s) => {
              const { streamData, cardThumbRec } = buildCard(s);
              const badgeLabel = getStatusBadge(s.status);
              const now = Date.now();
              const startTime = new Date(`${s.date}T${s.start}`).getTime();
              const endTime = new Date(`${s.date}T${s.end}`).getTime();
              const isLive = !badgeLabel && now >= startTime && now <= endTime;
              return (
                <Box
                  key={s.id}
                  sx={{
                    ...cardWrapSx,
                    width: CARD_WIDTH_H,
                    position: "relative",
                  }}
                >
                  {isLive && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        zIndex: 5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.2,
                        py: 0.5,
                        borderRadius: "999px",
                        bgcolor: "#d32f2f",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 0.8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
                        animation: "pulse 1.5s infinite",
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#fff",
                        }}
                      />
                      LIVE NOW
                    </Box>
                  )}
                  <StreamCard
                    stream={streamData}
                    thumbRec={cardThumbRec}
                    formatDate={formatDate}
                  />
                  {badgeLabel ? (
                    <StatusStamp label={badgeLabel} />
                  ) : (
                    <CountdownTimer date={s.date} start={s.start} />
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Right gradient overlay */}
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              zIndex: 2,
              width: 40,
              background:
                "linear-gradient(to left, rgba(0,0,0,0.45) 0%, transparent 100%)",
              borderRadius: "0 14px 14px 0",
              opacity: canNext ? 1 : 0,
              pointerEvents: "none",
              transition: "opacity 0.25s ease",
            }}
          />
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — RECENT STREAMS (new, below carousel)
      ══════════════════════════════════════════════════════════ */}
      <Box sx={{ mt: 5 }}>
        {/* Section header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <History sx={{ fontSize: 17, color: "text.secondary" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 750,
                  fontSize: "1rem",
                  letterSpacing: "-0.2px",
                  lineHeight: 1.2,
                }}
              >
                Recent Streams
              </Typography>
              <Typography
                sx={{ fontSize: 11.5, color: "text.disabled", lineHeight: 1 }}
              >
                Previously broadcast
              </Typography>
            </Box>
          </Box>

          {!recentLoading && recentStreams.length > 0 && (
            <Chip
              label={`${recentStreams.length} recordings`}
              size="small"
              sx={{
                height: 21,
                fontSize: 10.5,
                fontWeight: 600,
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                color: "text.secondary",
                border: "1px solid",
                borderColor: "divider",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Box>

        {/* List card */}
        <Box
          sx={{
            borderRadius: "14px",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
            bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
          }}
        >
          {recentLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  px: 2,
                  py: 1.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Skeleton variant="circular" width={42} height={42} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="55%" height={16} />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={13}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Skeleton
                  variant="rounded"
                  width={52}
                  height={20}
                  sx={{ borderRadius: 999 }}
                />
              </Box>
            ))
          ) : recentStreams.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5, px: 3 }}>
              <History sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: 13.5,
                  fontWeight: 500,
                }}
              >
                No recent streams found.
              </Typography>
            </Box>
          ) : (
            (() => {
              const PAGE_SIZE = 5;
              const pagedStreams = recentStreams.slice(
                recentPage * PAGE_SIZE,
                (recentPage + 1) * PAGE_SIZE,
              );
              const totalPages = Math.ceil(recentStreams.length / PAGE_SIZE);
              return (
                <>
                  {pagedStreams.map((s, index) => (
                    <React.Fragment key={s.id}>
                      {index > 0 && (
                        <Divider
                          sx={{
                            mx: 2,
                            borderColor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                          }}
                        />
                      )}
                      <RecentStreamRow
                        stream={s}
                        colors={colors}
                        isDark={isDark}
                        onClick={() => {
                          const link =
                            s.website_stream_link || s.youtube_stream_link;
                          if (link) window.open(link, "_blank");
                        }}
                      />
                    </React.Fragment>
                  ))}
                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        px: 2,
                        py: 1.25,
                        borderTop: "1px solid",
                        borderColor: isDark
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}
                      >
                        Page {recentPage + 1} of {totalPages}
                      </Typography>
                      <Tooltip title="Previous page">
                        <span>
                          <IconButton
                            size="small"
                            disabled={recentPage === 0}
                            onClick={() => setRecentPage((p) => p - 1)}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <KeyboardArrowLeft fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Next page">
                        <span>
                          <IconButton
                            size="small"
                            disabled={recentPage >= totalPages - 1}
                            onClick={() => setRecentPage((p) => p + 1)}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <KeyboardArrowRight fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </>
              );
            })()
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HorizontalStreamCarousel;
